import { useMemo } from "react"
import { AiToolCallCard } from "./ai-tool-call-card"
import type { AiMessage } from "@/lib/ai-types"

interface AiMessageBubbleProps {
  message: AiMessage
}

/**
 * Renders a simple markdown subset: bold, italic, code, lists, headers.
 * No external dependency needed for this lightweight CRM chat.
 */
function parseTableRow(line: string): string[] {
  return line.split("|").slice(1, -1).map((c) => c.trim())
}

function isSeparatorRow(line: string): boolean {
  return /^\|[\s\-:|]+\|$/.test(line.trim())
}

function renderTable(tableLines: string[], key: number): React.ReactNode {
  const headerCells = parseTableRow(tableLines[0])
  const dataRows = tableLines.slice(isSeparatorRow(tableLines[1]) ? 2 : 1)

  return (
    <div key={key} className="my-2 overflow-x-auto rounded-lg border border-border">
      <table className="w-full text-xs">
        <thead>
          <tr className="bg-muted/50">
            {headerCells.map((cell, j) => (
              <th key={j} className="px-2 py-1 text-left text-xs font-semibold text-foreground">
                {formatInline(cell)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {dataRows.filter((r) => !isSeparatorRow(r)).map((row, j) => (
            <tr key={j} className="border-t border-border">
              {parseTableRow(row).map((cell, k) => (
                <td key={k} className="px-2 py-1 text-muted-foreground">
                  {formatInline(cell)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function renderMarkdown(text: string): React.ReactNode {
  const lines = text.split("\n")
  const elements: React.ReactNode[] = []

  let i = 0
  while (i < lines.length) {
    const line = lines[i]
    const key = i

    // Table: collect consecutive | lines
    if (line.trimStart().startsWith("|") && line.trimEnd().endsWith("|")) {
      const tableLines: string[] = []
      while (i < lines.length && lines[i].trimStart().startsWith("|") && lines[i].trimEnd().endsWith("|")) {
        tableLines.push(lines[i])
        i++
      }
      if (tableLines.length >= 2) {
        elements.push(renderTable(tableLines, key))
      } else {
        elements.push(<p key={key}>{formatInline(tableLines[0])}</p>)
      }
      continue
    }

    i++
    // Headers
    if (line.startsWith("### ")) {
      elements.push(<h4 key={key} className="mt-2 mb-1 text-sm font-semibold">{formatInline(line.slice(4))}</h4>)
    } else if (line.startsWith("## ")) {
      elements.push(<h3 key={key} className="mt-2 mb-1 font-semibold">{formatInline(line.slice(3))}</h3>)
    } else if (line.startsWith("# ")) {
      elements.push(<h2 key={key} className="mt-2 mb-1 text-base font-bold">{formatInline(line.slice(2))}</h2>)
    }
    // Unordered list
    else if (line.match(/^[\-\*] /)) {
      elements.push(
        <div key={key} className="flex gap-1.5 pl-2">
          <span className="mt-1 text-muted-foreground">&bull;</span>
          <span>{formatInline(line.slice(2))}</span>
        </div>
      )
    }
    // Ordered list
    else if (line.match(/^\d+\. /)) {
      const match = line.match(/^(\d+)\. (.*)/)
      if (match) {
        elements.push(
          <div key={key} className="flex gap-1.5 pl-2">
            <span className="text-muted-foreground">{match[1]}.</span>
            <span>{formatInline(match[2])}</span>
          </div>
        )
      }
    }
    // Empty line
    else if (line.trim() === "") {
      elements.push(<div key={key} className="h-2" />)
    }
    // Regular paragraph
    else {
      elements.push(<p key={key}>{formatInline(line)}</p>)
    }
  }

  return <>{elements}</>
}

function formatInline(text: string): React.ReactNode {
  // Process inline code, bold, italic
  const parts: React.ReactNode[] = []
  let remaining = text
  let key = 0

  while (remaining.length > 0) {
    // Inline code
    const codeMatch = remaining.match(/^`([^`]+)`/)
    if (codeMatch) {
      parts.push(
        <code key={key++} className="rounded bg-muted px-1 py-0.5 text-xs font-mono">
          {codeMatch[1]}
        </code>
      )
      remaining = remaining.slice(codeMatch[0].length)
      continue
    }

    // Bold
    const boldMatch = remaining.match(/^\*\*(.+?)\*\*/)
    if (boldMatch) {
      parts.push(<strong key={key++}>{boldMatch[1]}</strong>)
      remaining = remaining.slice(boldMatch[0].length)
      continue
    }

    // Italic
    const italicMatch = remaining.match(/^\*(.+?)\*/)
    if (italicMatch) {
      parts.push(<em key={key++}>{italicMatch[1]}</em>)
      remaining = remaining.slice(italicMatch[0].length)
      continue
    }

    // Regular char
    const nextSpecial = remaining.search(/[`*]/)
    if (nextSpecial === -1) {
      parts.push(remaining)
      break
    } else if (nextSpecial === 0) {
      parts.push(remaining[0])
      remaining = remaining.slice(1)
    } else {
      parts.push(remaining.slice(0, nextSpecial))
      remaining = remaining.slice(nextSpecial)
    }
  }

  return <>{parts}</>
}

export function AiMessageBubble({ message }: AiMessageBubbleProps) {
  const isUser = message.role === "user"
  const isTool = message.role === "tool"

  // Don't render tool messages directly (they're shown as tool call cards)
  if (isTool) return null

  const content = useMemo(() => {
    if (!message.content) return null
    return renderMarkdown(message.content)
  }, [message.content])

  return (
    <div className={`flex ${isUser ? "justify-end" : ""}`}>
      {/* Content */}
      <div
        className={`max-w-[85%] text-sm leading-relaxed ${
          isUser
            ? "rounded-[10px] bg-black/[0.04] px-2.5 py-1.5 font-medium text-[var(--text-primary)] dark:bg-white/[0.08]"
            : "text-[var(--text-primary)]"
        }`}
      >
        {/* Tool calls */}
        {message.tool_calls?.map((tc) => (
          <AiToolCallCard key={tc.id} toolName={tc.name} status="done" />
        ))}

        {content}

        {message.status === "error" && (
          <p className="mt-1 text-xs text-red-500">Wystąpił błąd podczas przetwarzania.</p>
        )}
      </div>
    </div>
  )
}

/** Streaming bubble that shows content as it arrives */
export function AiStreamingBubble({
  content,
  toolCalls,
}: {
  content: string
  toolCalls: Map<string, { name: string; status: "running" | "done" }>
}) {
  const rendered = useMemo(() => {
    if (!content) return null
    return renderMarkdown(content)
  }, [content])

  return (
    <div>
      <div className="max-w-[85%] text-sm leading-relaxed text-[var(--text-primary)]">
        {Array.from(toolCalls.entries()).map(([id, tc]) => (
          <AiToolCallCard key={id} toolName={tc.name} status={tc.status} />
        ))}
        {rendered}
        {!content && toolCalls.size === 0 && (
          <div className="flex items-center gap-1.5">
            <div className="h-2 w-2 animate-pulse rounded-full bg-[var(--text-tertiary)]" />
            <div className="h-2 w-2 animate-pulse rounded-full bg-[var(--text-tertiary)] [animation-delay:150ms]" />
            <div className="h-2 w-2 animate-pulse rounded-full bg-[var(--text-tertiary)] [animation-delay:300ms]" />
          </div>
        )}
      </div>
    </div>
  )
}
